import { useCallback, useEffect, useState } from "react";

/**
 * useStickyState — like useState, but the value is mirrored to
 * sessionStorage (or localStorage) so it survives cross-route navigation
 * within the same tab.
 *
 * Designed for search-bar text and other "I don't want to retype this
 * every time I bounce between screens" ergonomics. `key` should be
 * globally unique (e.g. "realtors:search"). `storage` defaults to
 * sessionStorage so the value clears when the tab closes — safer for
 * shared devices than localStorage.
 */
export default function useStickyState(key, defaultValue = "", storage = "session") {
  const store =
    typeof window === "undefined"
      ? null
      : storage === "local"
      ? window.localStorage
      : window.sessionStorage;

  const [value, setValue] = useState(() => {
    if (!store) return defaultValue;
    try {
      const raw = store.getItem(key);
      return raw == null ? defaultValue : raw;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (!store) return;
    try {
      if (value === "" || value == null) store.removeItem(key);
      else store.setItem(key, value);
    } catch {
      // storage disabled / quota exceeded — just carry on with the state
      // in memory. No user-visible regression.
    }
  }, [key, value, store]);

  const reset = useCallback(() => setValue(defaultValue), [defaultValue]);

  return [value, setValue, reset];
}
